import { useEffect, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { Alert } from './Alert';
import { Button } from './Button';
import type { ButtonTone } from './Button';
import { Modal } from './Modal';

/* A question with two answers, asked over the page rather than inside it.
 *
 * **Rendered through `Modal`, which is where the `<dialog>` lifecycle now lives.** This component
 * held all of it inline — `showModal()`, the `onCancel` that catches Escape *and* the platform's
 * backdrop dismissal, the target comparison that separates a backdrop click from a click on the
 * content, the generated id for `aria-labelledby`. Every one of those is a fact about `<dialog>`
 * rather than about confirming something, and the add-user form needed the same four. Two copies
 * would be two answers to "what does Escape do" in the one place a disagreement is invisible, because
 * both read correctly in isolation.
 *
 * It is the same box on screen: `size="sentence"` is `.lq-confirm`, the width rule this component
 * always had.
 *
 * **No close glyph** — `closable={false}`, argued at the prop in `Modal`. Two labelled answers, not
 * three, one of them unlabelled and ahead of Cancel in tab order.
 *
 * **Focus lands on Cancel, not Confirm**, whenever the tone is destructive. A modal that opens with
 * the destructive button focused turns a stray Enter — from the keypress that opened it, or from a
 * reader who types ahead — into the action itself. The safe answer is the one under the cursor.
 *
 * **The error is rendered inside the dialog rather than behind it.** The first version of the delete
 * flow closed on refusal and showed the reason on the card underneath, which put the answer somewhere
 * other than where the question was asked — the reader's attention is here. A caller keeps `open`
 * true and passes `error`; the dialog stays up with the reason in it, which is also what lets a
 * refusal be read and acted on rather than dismissed.
 *
 * **`message` is a node, not a string.** Some confirmations need a name in bold or a count that came
 * from a different request, and a caller that could only pass text would build the sentence by
 * concatenation — which is how a name with a `%` in it ends up in a format string.
 */

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  /** `danger` also decides where focus lands — see the note above. */
  readonly tone?: ButtonTone;
  /** Disables the confirm and shows a spinner. Cancel stays live: see `onCancel`. */
  readonly busy?: boolean;
  /** Holds the confirm until the caller is satisfied — a destructive confirm that asks for the
   * resource's name typed out keeps its button disabled until it matches. Distinct from `busy`,
   * which means "already happening": the two read differently and must not be collapsed. */
  readonly confirmDisabled?: boolean;
  /** Rendered inside the dialog. The caller keeps `open` true to show it. */
  readonly error?: string | undefined;
  /** A modifier on the surface, forwarded to `Modal` — argued at the prop there. Used by the one
   * screen restyled to the reference so far; the class carries the difference rather than
   * `.lq-confirm` itself, so the other four callers of this component are untouched. */
  readonly className?: string | undefined;
  readonly onConfirm: () => void;
  /** Escape, the backdrop, and the cancel button all arrive here — one path, so a caller cannot
   * handle two of the three and forget the last. */
  readonly onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  busy = false,
  confirmDisabled = false,
  error,
  className,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactElement {
  /* No `if (!open) return null` any more: `Modal` renders nothing while closed, and it does so for
   * this component's original reason — keeping the subtree mounted would preserve a stale `error`
   * across two different confirmations of the same kind, and the second would open showing the
   * first's refusal. Repeating the early return here would be a second guard on one fact. */
  const actions = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /* Focus moves to the *first* button of the action row, which is Cancel by construction — the same
     * fact that makes it the first tab stop. Set here rather than with `autoFocus`, because
     * `showModal` moves focus itself and an autofocused element is not reliably honoured after it.
     *
     * The ref is on the row rather than on the dialog, which is what changed with the extraction: the
     * `<dialog>` node belongs to `Modal` now and this component has no handle on it. Scoping the query
     * to the row is the better shape anyway — the old version queried the dialog's *first* button and
     * would have found `Modal`'s close glyph the moment one was rendered, so it was correct only
     * because nothing else preceded Cancel. `closable={false}` and this scope now hold that property
     * for two independent reasons.
     *
     * Queried rather than held in a ref so `Button` needs no `forwardRef`: this is React 18, where a
     * function component takes no `ref` prop, and widening the kit's most-used component to focus a
     * dialog would be a large change for a narrow need. The DOM order is the contract, and the test
     * beside this file asserts it.
     *
     * `Modal` is the child, so its `showModal()` effect has already run when this one does — React
     * flushes effects bottom-up. Focusing before the dialog was shown would be discarded. */
    if (tone === 'danger') actions.current?.querySelector('button')?.focus();
  }, [open, tone]);

  return (
    <Modal
      open={open}
      title={title}
      size="sentence"
      closable={false}
      className={className}
      /* Escape, the backdrop and the cancel button all arrive at one handler — `Modal` routes the
       * first two here, which is what this component used to do for itself. */
      onClose={onCancel}
    >
      <div className="lq-confirm__body">{message}</div>

      {error === undefined ? null : (
        /* `warning`, not `error`: the most common reason a confirmed action does not happen in this
         * product is that the platform *declined* it — a source still in use, a state that forbids
         * it — which is the system working. A red banner would read as a fault. */
        <Alert tone="warning">{error}</Alert>
      )}

      <div className="lq-confirm__actions" ref={actions}>
        {/* Cancel first, in the markup *and* on screen. For a destructive confirm the safe answer
            should be the first thing read, the first tab stop and where focus lands; the destructive
            one goes last. An earlier version reversed the row visually and the comment describing it
            was the wrong way round — see `.lq-confirm__actions`. */}
        <Button onClick={onCancel}>{cancelLabel}</Button>
        <Button tone={tone} busy={busy} disabled={confirmDisabled} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
